import { Cookie, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '../i18n';
import { getConsentValue, setConsent } from '../utils/cookieConsent';

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const show = () => {
    setMounted(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  };

  const hide = () => {
    setVisible(false);
    setTimeout(() => setMounted(false), 350);
  };

  useEffect(() => {
    const stored = getConsentValue();
    if (!stored) {
      const timer = setTimeout(show, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for external resets (e.g. from Settings page)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) show();
    };
    window.addEventListener('cookie-consent-change', handler);
    return () => window.removeEventListener('cookie-consent-change', handler);
  }, []);

  const handleAcceptAll = () => {
    setConsent('accepted');
    hide();
  };

  const handleEssentialOnly = () => {
    setConsent('essential-only');
    hide();
  };

  if (!mounted) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6 pointer-events-none"
      style={{
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.35s cubic-bezier(0.12, 1, 0.4, 1), opacity 0.35s ease',
      }}
    >
      <div className="max-w-lg mx-auto pointer-events-auto">
        <div className="bg-gray-800 border border-gray-600 rounded-2xl shadow-2xl shadow-black/40 p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Cookie className="h-5 w-5 text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-100 text-sm">{t('cookies.title')}</h3>
                <button
                  onClick={handleEssentialOnly}
                  className="text-gray-500 hover:text-gray-300 -mr-1"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                {t('cookies.description')}{' '}
                <Link
                  to="/privacy"
                  className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
                >
                  Privacy Policy
                </Link>
              </p>
              <div className="flex gap-3">
                <button onClick={handleAcceptAll} className="btn-primary text-sm px-4 py-2">
                  {t('cookies.acceptAll')}
                </button>
                <button onClick={handleEssentialOnly} className="btn-ghost text-sm px-4 py-2">
                  {t('cookies.essentialOnly')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
